import { useEffect, useMemo, useRef, useState } from "react";
import { AuraTelemetryEventLog } from "@auraone/aura-ide-kit";
import { commands, datasets, probeTrials } from "./data";
import {
  buildVirtualWindow,
  clampTime,
  defaultFilters,
  defaultSort,
  exportManifest,
  formatTime,
  summarizeDataset,
  visibleEpisodes,
} from "./core";
import {
  buildDeterministicClusters,
  createDatasetFromLocalFiles,
  mergeClusterRows,
  recomputeDeterministicClusters,
  refreshClusterRows,
  splitClusterRows,
  type LocalFileEntry,
} from "./localOperations";
import {
  LocalDiagnosticEventBuffer,
  createRoboticsDiagnosticEvent,
  ensureRoboticsIntakeInstallSigningKeypair,
  roboticsIntakeInstallSigningKeypairKey,
  toAuraTelemetryEvents,
  type LocalDiagnosticEntry,
} from "./platformContracts";
import type {
  AppNotice,
  Cluster,
  CommandId,
  DatasetTab,
  Density,
  Episode,
  ExportTarget,
  FilterState,
  Intervention,
  OperationState,
  SensorStream,
  SensorVisibilityStore,
  SortField,
  SortState,
  UpdateState,
  ViewMode,
} from "./types";
import { clampIndex, useMediaQuery } from "./components";

const sensorVisibilityStorageKey = "robostudio:sensor-visibility:v1";

function readSensorVisibilityStore(): SensorVisibilityStore {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(sensorVisibilityStorageKey);
    return raw ? (JSON.parse(raw) as SensorVisibilityStore) : {};
  } catch {
    return {};
  }
}

function applyPersistedSensorVisibility(
  sourceTabs: DatasetTab[],
): DatasetTab[] {
  const store = readSensorVisibilityStore();
  return sourceTabs.map((tab) => ({
    ...tab,
    episodes: tab.episodes.map((episode) => ({
      ...episode,
      sensors: episode.sensors.map((sensor) => {
        const visible = store[`${tab.id}:${sensor.id}`];
        return typeof visible === "boolean" ? { ...sensor, visible } : sensor;
      }),
    })),
  }));
}

function sensorVisibilityStore(tabs: DatasetTab[]): SensorVisibilityStore {
  return Object.fromEntries(
    tabs.flatMap((tab) =>
      tab.episodes.flatMap((episode) =>
        episode.sensors.map(
          (sensor) => [`${tab.id}:${sensor.id}`, sensor.visible] as const,
        ),
      ),
    ),
  );
}

export function useStudioController() {
const [tabs, setTabs] = useState<DatasetTab[]>(() =>
  applyPersistedSensorVisibility(datasets),
);
const [activeTabId, setActiveTabId] = useState(datasets[0].id);
const [view, setView] = useState<ViewMode>("browse");
const [filters, setFilters] = useState<FilterState>(defaultFilters);
const [sort, setSort] = useState<SortState>(defaultSort);
const [selectedEpisodeId, setSelectedEpisodeId] = useState(
  datasets[0].episodes[0].id,
);
const [cursor, setCursor] = useState(4.21);
const [playing, setPlaying] = useState(false);
const [speed, setSpeed] = useState(1);
const [density, setDensity] = useState<Density>("comfortable");
const [mediaDark, setMediaDark] = useState(true);
const [sidebarOpen, setSidebarOpen] = useState(false);
const [inspectorOpen, setInspectorOpen] = useState(false);
const [activeSensorId, setActiveSensorId] = useState("cam-front");
const [showOnboarding, setShowOnboarding] = useState(true);
const [showPalette, setShowPalette] = useState(false);
const [commandQuery, setCommandQuery] = useState("");
const [gridOffset, setGridOffset] = useState(0);
const [exportTarget, setExportTarget] = useState<ExportTarget>("Local disk");
const [diagnosticBufferEnabled, setDiagnosticBufferEnabled] = useState(false);
const [diagnosticBuffer] = useState(() => new LocalDiagnosticEventBuffer());
const [diagnosticEntries, setDiagnosticEntries] = useState<
  LocalDiagnosticEntry[]
>([]);
const [networkOnline, setNetworkOnline] = useState(
  () => typeof navigator === "undefined" || navigator.onLine,
);
const [updateState, setUpdateState] = useState<UpdateState>("unsupported");
const [clusterRows, setClusterRows] = useState<Cluster[]>(() =>
  buildDeterministicClusters(datasets[0].episodes),
);
const [clusterHistory, setClusterHistory] = useState<Cluster[][]>([]);
const [clusterRunState, setClusterRunState] =
  useState<OperationState>("idle");
const [clusterProgress, setClusterProgress] = useState(0);
const [intakeState, setIntakeState] = useState<OperationState>("idle");
const [intakeMessage, setIntakeMessage] = useState(
  "No browser-selected dataset is open.",
);
const [notice, setNotice] = useState<AppNotice | null>(null);
const navigationTriggerRef = useRef<HTMLButtonElement>(null);
const navigationCloseRef = useRef<HTMLButtonElement>(null);
const inspectorTriggerRef = useRef<HTMLButtonElement>(null);
const inspectorCloseRef = useRef<HTMLButtonElement>(null);
const openDatasetButtonRef = useRef<HTMLButtonElement>(null);
const fileInputRef = useRef<HTMLInputElement>(null);
const directoryInputRef = useRef<HTMLInputElement>(null);
const paletteRestoreRef = useRef<HTMLElement | null>(null);
const sidebarRef = useRef<HTMLElement>(null);
const inspectorRef = useRef<HTMLElement>(null);
const mobileDrawerLayout = useMediaQuery("(max-width: 760px)");
const inspectorDrawerLayout = useMediaQuery("(max-width: 1180px)");

const activeDataset = tabs.find((tab) => tab.id === activeTabId) ?? tabs[0];
const filteredEpisodes = useMemo(
  () => visibleEpisodes(activeDataset.episodes, filters, sort),
  [activeDataset.episodes, filters, sort],
);
const visibleGrid = useMemo(
  () =>
    buildVirtualWindow(
      filteredEpisodes,
      gridOffset,
      density === "ultra" ? 36 : density === "compact" ? 24 : 16,
    ),
  [density, filteredEpisodes, gridOffset],
);
const selectedEpisode =
  activeDataset.episodes.find(
    (episode) => episode.id === selectedEpisodeId,
  ) ??
  filteredEpisodes[0] ??
  activeDataset.episodes[0];
const summary = summarizeDataset(activeDataset);
const manifest = exportManifest(
  activeDataset,
  filteredEpisodes,
  exportTarget,
);

useEffect(() => {
  const syncNetwork = () => setNetworkOnline(navigator.onLine);
  window.addEventListener("online", syncNetwork);
  window.addEventListener("offline", syncNetwork);
  return () => {
    window.removeEventListener("online", syncNetwork);
    window.removeEventListener("offline", syncNetwork);
  };
}, []);

useEffect(() => {
  const duration = selectedEpisode.duration;
  if (!playing || duration === null) {
    if (playing) setPlaying(false);
    return;
  }
  let frame = 0;
  let previous = performance.now();
  const tick = (now: number) => {
    const elapsedSeconds = Math.min(0.25, (now - previous) / 1000);
    previous = now;
    setCursor((current) => {
      const next = clampTime(current + elapsedSeconds * speed, duration);
      if (next >= duration) {
        setPlaying(false);
        return duration;
      }
      return next;
    });
    frame = requestAnimationFrame(tick);
  };
  frame = requestAnimationFrame(tick);
  return () => cancelAnimationFrame(frame);
}, [playing, selectedEpisode.duration, speed]);

useEffect(() => {
  const modalOpen = showOnboarding || showPalette;
  const background = Array.from(
    document.querySelectorAll<HTMLElement>(".topbar,.layout,.statusbar"),
  );
  for (const element of background) {
    element.inert = modalOpen;
    if (modalOpen) {
      element.setAttribute("aria-hidden", "true");
    } else {
      element.removeAttribute("aria-hidden");
    }
  }
  return () => {
    for (const element of background) {
      element.inert = false;
      element.removeAttribute("aria-hidden");
    }
  };
}, [showOnboarding, showPalette]);

useEffect(() => {
  if (!notice || notice.onAction) return;
  const timeout = window.setTimeout(() => setNotice(null), 4_000);
  return () => window.clearTimeout(timeout);
}, [notice]);

useEffect(() => {
  const onKeyDown = (event: KeyboardEvent) => {
    const target = event.target as HTMLElement | null;
    const isInteractive = Boolean(
      target?.closest(
        "button,a,input,textarea,select,summary,[role='button'],[contenteditable='true']",
      ),
    );
    const mod = event.metaKey || event.ctrlKey;
    if (mod && event.key.toLowerCase() === "k") {
      event.preventDefault();
      if (showOnboarding) return;
      if (showPalette) closeCommandPalette();
      else openCommandPalette();
      return;
    }
    if (mod && event.key.toLowerCase() === "o") {
      event.preventDefault();
      openLocalFilePicker();
      return;
    }
    if (mod && event.key.toLowerCase() === "p") {
      event.preventDefault();
      executeCommand("quick-switch");
      return;
    }
    if (mod && event.shiftKey && event.key.toLowerCase() === "v") {
      event.preventDefault();
      executeCommand("save-view");
      return;
    }
    if (mod && event.shiftKey && event.key.toLowerCase() === "e") {
      event.preventDefault();
      setView("export");
      return;
    }
    if (mod && event.shiftKey && event.key.toLowerCase() === "a") {
      event.preventDefault();
      setView("export");
      setExportTarget("AuraOne Programs");
      return;
    }
    if (event.key === "Escape") {
      if (showPalette) {
        closeCommandPalette();
        return;
      }
      if (showOnboarding) {
        closeOnboarding();
        return;
      }
      if (sidebarOpen) navigationTriggerRef.current?.focus();
      if (inspectorOpen) inspectorTriggerRef.current?.focus();
      setSidebarOpen(false);
      setInspectorOpen(false);
      return;
    }
    if (isInteractive) return;
    if (event.key === " ") {
      event.preventDefault();
      if (selectedEpisode.duration !== null) setPlaying((value) => !value);
    }
    if (event.key === "ArrowRight")
      setCursor((value) =>
        clampTime(
          value + (event.shiftKey ? 10 / 30 : 1 / 30),
          selectedEpisode.duration,
        ),
      );
    if (event.key === "ArrowLeft")
      setCursor((value) =>
        clampTime(
          value - (event.shiftKey ? 10 / 30 : 1 / 30),
          selectedEpisode.duration,
        ),
      );
    if (!mod && event.key.toLowerCase() === "j") chooseRelativeEpisode(1);
    if (!mod && event.key.toLowerCase() === "k") chooseRelativeEpisode(-1);
    if (!mod && event.key.toLowerCase() === "t") executeCommand("tag-picker");
    if (!mod && event.key.toLowerCase() === "b") executeCommand("add-phase");
    if (!mod && event.key.toLowerCase() === "r")
      updateEpisode(selectedEpisode.id, {
        reviewed:
          selectedEpisode.reviewed === "reviewed" ? "unreviewed" : "reviewed",
      });
    if (!mod && event.key.toLowerCase() === "s")
      updateEpisode(selectedEpisode.id, { success: "success" });
    if (!mod && event.key.toLowerCase() === "f")
      updateEpisode(selectedEpisode.id, { success: "failure" });
    if (!mod && event.key.toLowerCase() === "c") setView("clusters");
    if (!mod && event.key.toLowerCase() === "v") setView("probe");
    if (!mod && event.key.toLowerCase() === "q") setView("qa");
    if (/^[1-8]$/.test(event.key) && !mod)
      setSpeed(
        Number(event.key) <= 4
          ? [0.1, 0.25, 0.5, 1][Number(event.key) - 1]
          : [2, 4, 8, 1][Number(event.key) - 5],
      );
    if (/^[1-8]$/.test(event.key) && mod) {
      event.preventDefault();
      const tab = tabs[Number(event.key) - 1];
      if (tab) setActiveTabId(tab.id);
    }
  };

  window.addEventListener("keydown", onKeyDown);
  return () => window.removeEventListener("keydown", onKeyDown);
}, [
  activeTabId,
  cursor,
  filteredEpisodes,
  filters,
  inspectorOpen,
  selectedEpisode,
  showOnboarding,
  showPalette,
  sidebarOpen,
  sort,
  tabs,
]);

useEffect(() => {
  if (sidebarOpen) navigationCloseRef.current?.focus();
}, [sidebarOpen]);

useEffect(() => {
  if (inspectorOpen) inspectorCloseRef.current?.focus();
}, [inspectorOpen]);

useEffect(() => {
  if (sidebarRef.current) {
    sidebarRef.current.inert = mobileDrawerLayout && !sidebarOpen;
  }
}, [mobileDrawerLayout, sidebarOpen]);

useEffect(() => {
  if (inspectorRef.current) {
    inspectorRef.current.inert = inspectorDrawerLayout && !inspectorOpen;
  }
}, [inspectorDrawerLayout, inspectorOpen]);

useEffect(() => {
  setClusterRows(buildDeterministicClusters(activeDataset.episodes));
  setClusterHistory([]);
  setClusterRunState("idle");
  setClusterProgress(0);
}, [activeTabId]);

useEffect(() => {
  try {
    window.localStorage.setItem(
      sensorVisibilityStorageKey,
      JSON.stringify(sensorVisibilityStore(tabs)),
    );
  } catch {
    // Persistence is best-effort; review state remains in-memory if storage is unavailable.
  }
}, [tabs]);

function updateFilters(partial: Partial<FilterState>) {
  setGridOffset(0);
  setFilters((current) => ({ ...current, ...partial }));
}

function updateEpisode(episodeId: string, partial: Partial<Episode>) {
  const updatedEpisodes = activeDataset.episodes.map((episode) =>
    episode.id === episodeId ? { ...episode, ...partial } : episode,
  );
  setTabs((currentTabs) =>
    currentTabs.map((tab) => ({
      ...tab,
      lastSaved: "now",
      episodes: tab.id === activeDataset.id ? updatedEpisodes : tab.episodes,
    })),
  );
  setClusterRows((current) => refreshClusterRows(current, updatedEpisodes));
}

function chooseEpisode(episode: Episode) {
  recordDiagnostic("robotics_feature_used", { feature_id: "episode.scrub" });
  setSelectedEpisodeId(episode.id);
  setCursor(0);
  setPlaying(false);
  setView("scrub");
}

function chooseRelativeEpisode(delta: number) {
  const index = filteredEpisodes.findIndex(
    (episode) => episode.id === selectedEpisode.id,
  );
  const next =
    filteredEpisodes[clampIndex(index + delta, filteredEpisodes.length)];
  if (next) chooseEpisode(next);
}

function openSampleDataset() {
  recordDiagnostic("robotics_dataset_opened", {
    format: "lerobot_v3_metadata",
    episode_count: 96,
    repository_fixture: true,
  });
  setActiveTabId("so101");
  setSelectedEpisodeId(datasets[0].episodes[0].id);
  setCursor(0);
  setPlaying(false);
  setIntakeState("success");
  setIntakeMessage("Bundled synthetic sample loaded: 96 fixture episodes.");
  setShowOnboarding(false);
  setView("browse");
}

function openLocalFilePicker() {
  fileInputRef.current?.click();
}

function openLocalDirectoryPicker() {
  directoryInputRef.current?.click();
}

async function handleLocalSelection(files: FileList | null) {
  if (!files?.length) return;
  setIntakeState("running");
  setIntakeMessage(
    `Reading ${files.length} browser-selected file${files.length === 1 ? "" : "s"} locally...`,
  );
  try {
    const entries: LocalFileEntry[] = Array.from(files).map((file) => ({
      name: file.name,
      relativePath: file.webkitRelativePath || undefined,
      text: () => file.text(),
    }));
    const dataset = await createDatasetFromLocalFiles(entries);
    setTabs((current) => [
      dataset,
      ...current.filter((tab) => tab.id !== dataset.id),
    ]);
    setActiveTabId(dataset.id);
    setSelectedEpisodeId(dataset.episodes[0].id);
    setFilters(defaultFilters);
    setSort(defaultSort);
    setGridOffset(0);
    setCursor(0);
    setPlaying(false);
    setView("browse");
    setShowOnboarding(false);
    setIntakeState("success");
    setIntakeMessage(
      `Opened ${dataset.name}: ${dataset.episodes.length} local episode record${dataset.episodes.length === 1 ? "" : "s"}.`,
    );
    setNotice({
      tone: "success",
      message: `Local dataset ${dataset.name} is ready for review.`,
    });
    setClusterRows(buildDeterministicClusters(dataset.episodes));
    setClusterHistory([]);
    recordDiagnostic("robotics_dataset_opened", {
      format: dataset.format,
      episode_count: dataset.episodes.length,
      local_manifest: true,
    });
    window.requestAnimationFrame(() => openDatasetButtonRef.current?.focus());
  } catch (error) {
    const message =
      error instanceof Error
        ? error.message
        : "The selected local input could not be opened.";
    setIntakeState("error");
    setIntakeMessage(message);
    setNotice({ tone: "danger", message });
  } finally {
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (directoryInputRef.current) directoryInputRef.current.value = "";
  }
}

function openCommandPalette() {
  paletteRestoreRef.current =
    document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
  setShowPalette(true);
}

function closeCommandPalette() {
  setShowPalette(false);
  window.requestAnimationFrame(() => paletteRestoreRef.current?.focus());
}

function closeOnboarding() {
  setShowOnboarding(false);
  window.requestAnimationFrame(() => openDatasetButtonRef.current?.focus());
}

function runClustering() {
  if (clusterRunState === "running") return;
  setClusterRunState("running");
  setClusterProgress(10);
  setNotice({
    tone: "info",
    message: "Grouping explicit failure metadata from the active dataset.",
  });
  const interval = window.setInterval(() => {
    setClusterProgress((progress) => {
      const next = Math.min(100, progress + 30);
      if (next >= 100) {
        window.clearInterval(interval);
        setClusterRows((current) => {
          setClusterHistory((history) => [...history, current]);
          return recomputeDeterministicClusters(
            current,
            activeDataset.episodes,
          );
        });
        setClusterRunState("success");
        const nextCount = buildDeterministicClusters(
          activeDataset.episodes,
        ).length;
        setNotice({
          tone: nextCount ? "success" : "warning",
          message: nextCount
            ? `Recomputed ${nextCount} deterministic clusters from explicit failure fields.`
            : "No explicit failure clusters or failure tags were present; no clusters were fabricated.",
        });
      }
      return next;
    });
  }, 120);
}

function splitCluster(clusterId: string) {
  setClusterRows((current) => {
    const next = splitClusterRows(current, clusterId, activeDataset.episodes);
    if (next.length === current.length) {
      setNotice({
        tone: "warning",
        message: "This cluster cannot be split further.",
      });
      return current;
    }
    setClusterHistory((history) => [...history, current]);
    setNotice({
      tone: "success",
      message: `Split ${clusterId} into two deterministic review segments.`,
      actionLabel: "Undo",
      onAction: undoClusters,
    });
    return next;
  });
}

function mergeCluster(clusterId: string) {
  setClusterRows((current) => {
    const next = mergeClusterRows(current, clusterId, activeDataset.episodes);
    if (next.length === current.length) {
      setNotice({
        tone: "warning",
        message: "A second cluster is required before merging.",
      });
      return current;
    }
    setClusterHistory((history) => [...history, current]);
    setNotice({
      tone: "success",
      message: `Merged ${clusterId} with its nearest comparison cluster.`,
      actionLabel: "Undo",
      onAction: undoClusters,
    });
    return next;
  });
}

function undoClusters() {
  setClusterHistory((history) => {
    const previous = history.at(-1);
    if (previous) {
      setClusterRows(refreshClusterRows(previous, activeDataset.episodes));
      setNotice({
        tone: "info",
        message: "Restored the previous cluster state.",
      });
    }
    return history.slice(0, -1);
  });
}

function executeCommand(commandId: CommandId) {
  if (commandId === "open-dataset") openLocalFilePicker();
  if (commandId === "quick-switch") {
    const index = tabs.findIndex((tab) => tab.id === activeTabId);
    const next = tabs[(index + 1) % tabs.length];
    if (next && next.id !== activeTabId) {
      setActiveTabId(next.id);
    } else {
      setNotice({
        tone: "info",
        message:
          "Only one dataset is open. Import a JSON or JSONL manifest to enable quick switching.",
      });
    }
  }
  if (commandId === "next-episode") chooseRelativeEpisode(1);
  if (commandId === "previous-episode") chooseRelativeEpisode(-1);
  if (commandId === "toggle-play" && selectedEpisode.duration !== null)
    setPlaying((current) => !current);
  if (commandId === "frame-back")
    setCursor((current) =>
      clampTime(current - 1 / 30, selectedEpisode.duration),
    );
  if (commandId === "frame-forward")
    setCursor((current) =>
      clampTime(current + 1 / 30, selectedEpisode.duration),
    );
  if (commandId === "tag-picker") {
    updateEpisode(selectedEpisode.id, {
      taxonomyTags: selectedEpisode.taxonomyTags.includes(
        "org:custom:needs_second_pass",
      )
        ? selectedEpisode.taxonomyTags
        : [...selectedEpisode.taxonomyTags, "org:custom:needs_second_pass"],
    });
    setNotice({
      tone: "success",
      message: "Applied needs_second_pass to the selected episode.",
    });
  }
  if (commandId === "toggle-reviewed")
    updateEpisode(selectedEpisode.id, {
      reviewed:
        selectedEpisode.reviewed === "reviewed" ? "unreviewed" : "reviewed",
    });
  if (commandId === "mark-success")
    updateEpisode(selectedEpisode.id, { success: "success" });
  if (commandId === "mark-failure")
    updateEpisode(selectedEpisode.id, { success: "failure" });
  if (commandId === "add-phase") {
    if (selectedEpisode.duration === null) {
      setNotice({
        tone: "warning",
        message:
          "This imported record has no duration, so a timed phase boundary cannot be added.",
      });
      closeCommandPalette();
      return;
    }
    updateEpisode(selectedEpisode.id, {
      phases: [
        ...selectedEpisode.phases,
        {
          id: `review-boundary-${selectedEpisode.phases.length + 1}`,
          label: "review boundary",
          start: cursor,
          end: Math.min(selectedEpisode.duration, cursor + 0.5),
        },
      ],
    });
    setNotice({
      tone: "success",
      message: `Added a review boundary at ${formatTime(cursor)}.`,
    });
  }
  if (commandId === "clusters") setView("clusters");
  if (commandId === "probe") setView("probe");
  if (commandId === "qa") setView("qa");
  if (commandId === "export") setView("export");
  if (commandId === "programs-intake") {
    setExportTarget("AuraOne Programs");
    setView("export");
  }
  if (commandId === "save-view") {
    setTabs((current) =>
      current.map((tab) =>
        tab.id === activeDataset.id
          ? {
              ...tab,
              savedViews: [
                ...tab.savedViews,
                {
                  id: `saved-${tab.savedViews.length + 1}`,
                  name: `Review view ${tab.savedViews.length + 1}`,
                  filters,
                  sort,
                },
              ],
            }
          : tab,
      ),
    );
    setNotice({
      tone: "success",
      message: "Saved the current filters and sort locally.",
    });
  }
  closeCommandPalette();
}

function recordDiagnostic(
  eventName:
    | "robotics_dataset_opened"
    | "robotics_export_completed"
    | "robotics_feature_used",
  payload: Record<string, string | number | boolean>,
) {
  if (!diagnosticBufferEnabled) return;
  diagnosticBuffer.record(createRoboticsDiagnosticEvent(eventName, payload));
  setDiagnosticEntries([...diagnosticBuffer.list()].reverse());
}
  return {
    activeDataset, activeSensorId, activeTabId, chooseEpisode,
    closeCommandPalette, closeOnboarding, clusterProgress, clusterRows,
    clusterRunState, commandQuery, cursor, density,
    diagnosticBufferEnabled, diagnosticEntries, directoryInputRef,
    executeCommand, exportTarget, fileInputRef, filteredEpisodes, filters,
    gridOffset, handleLocalSelection, inspectorCloseRef,
    inspectorDrawerLayout, inspectorOpen, inspectorRef, inspectorTriggerRef,
    intakeMessage, intakeState, manifest, mediaDark, mergeCluster,
    mobileDrawerLayout, navigationCloseRef, navigationTriggerRef,
    networkOnline, notice, openCommandPalette, openDatasetButtonRef,
    openLocalDirectoryPicker, openLocalFilePicker, openSampleDataset, playing,
    recordDiagnostic, runClustering, selectedEpisode, setActiveSensorId,
    setActiveTabId, setCommandQuery, setCursor, setDensity,
    setDiagnosticBufferEnabled, setExportTarget, setFilters, setGridOffset,
    setInspectorOpen, setMediaDark, setNotice, setPlaying, setSidebarOpen,
    setSort, setSpeed, setUpdateState, setView, showOnboarding, showPalette,
    sidebarOpen, sidebarRef, sort, speed, splitCluster, summary, tabs,
    updateEpisode, updateFilters, updateState, view, visibleGrid,
    setTabs, setClusterRows, setClusterHistory, setClusterRunState,
    setClusterProgress, setIntakeState, setIntakeMessage,
    datasets, setSelectedEpisodeId,
    setShowOnboarding, setShowPalette, setDiagnosticEntries,
    setNetworkOnline,
  };
}
